/**
 * User-level event store (framework-free, unit-testable).
 *
 * Holds the user-scoped channels that are not tied to a task: credit updates
 * (`credit_update`) and in-app notifications (`notification`). Kept apart
 * from the task store so credit/notification consumers never re-render on
 * task progress, and task consumers never re-render on these.
 */

import { logDebug, type CreditUpdateEvent, type NotificationEvent, type WebSocketMessage } from './types';

export interface UserEventSnapshot {
  lastCreditUpdate: CreditUpdateEvent | null;
  unreadNotificationCount: number;
  latestNotification: NotificationEvent | null;
}

type Listener = () => void;

const EMPTY: UserEventSnapshot = {
  lastCreditUpdate: null,
  unreadNotificationCount: 0,
  latestNotification: null,
};

export class UserEventStore {
  private snapshot: UserEventSnapshot = EMPTY;
  private listeners = new Set<Listener>();

  getSnapshot = (): UserEventSnapshot => this.snapshot;

  listen = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  private update(patch: Partial<UserEventSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch };
    Array.from(this.listeners).forEach((listener) => listener());
  }

  /** Same contract as a React state setter (value or updater). */
  setUnreadNotificationCount = (value: number | ((prev: number) => number)): void => {
    const next = typeof value === 'function' ? value(this.snapshot.unreadNotificationCount) : value;
    if (next === this.snapshot.unreadNotificationCount) return;
    this.update({ unreadNotificationCount: next });
  };

  clearLatestNotification = (): void => {
    if (this.snapshot.latestNotification === null) return;
    this.update({ latestNotification: null });
  };

  /** Drop all per-user state (logout / user switch, FE-20). */
  reset(): void {
    if (this.snapshot === EMPTY) return;
    this.snapshot = EMPTY;
    Array.from(this.listeners).forEach((listener) => listener());
  }

  /** Applies a user-scoped server message. Returns false for types it does not own. */
  handleMessage(message: WebSocketMessage): boolean {
    switch (message.type) {
      case 'credit_update':
        logDebug('[WebSocket] Credit update:', message.data);
        this.update({
          lastCreditUpdate: {
            action: message.data?.action,
            creditsAdded: message.data?.credits_added,
            packId: message.data?.pack_id,
            error: message.data?.error,
            timestamp: Date.now(),
          },
        });
        return true;

      case 'notification':
        logDebug('[WebSocket] Notification:', message.data);
        // Bump unread count and keep the latest one for a toast/banner
        this.update({
          unreadNotificationCount: this.snapshot.unreadNotificationCount + 1,
          latestNotification: message.data as NotificationEvent,
        });
        return true;

      default:
        return false;
    }
  }
}

